import { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface CustomerLocationPickerProps {
  latitude: number | null;
  longitude: number | null;
  onChange: (location: { latitude: number; longitude: number }) => void;
}

interface LongdoLocation {
  lat: number;
  lon: number;
}

interface LongdoMapInstance {
  location: {
    (location: LongdoLocation, zoom?: boolean): void;
    (mode?: unknown): LongdoLocation;
  };
  Overlays: {
    add: (marker: unknown) => void;
    clear: () => void;
  };
  Event: {
    bind: (event: string, callback: () => void) => void;
  };
}

interface LongdoGlobal {
  Map: new (options: { placeholder: HTMLElement }) => LongdoMapInstance;
  Marker: new (location: LongdoLocation) => unknown;
  LocationMode: {
    Pointer: unknown;
  };
}

declare global {
  interface Window {
    longdo?: LongdoGlobal;
    __longdoMapLoadingPromise?: Promise<void>;
  }
}

const DEFAULT_CENTER = {
  latitude: 13.7563,
  longitude: 100.5018,
};

const loadLongdoMap = (apiKey: string) => {
  if (window.longdo) return Promise.resolve();
  if (window.__longdoMapLoadingPromise) return window.__longdoMapLoadingPromise;

  window.__longdoMapLoadingPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-longdo-map="true"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error("Longdo Map failed to load")),
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://api.longdo.com/map/?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.longdoMap = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Longdo Map failed to load"));
    document.body.appendChild(script);
  });

  return window.__longdoMapLoadingPromise;
};

export default function CustomerLocationPicker({
  latitude,
  longitude,
  onChange,
}: CustomerLocationPickerProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LongdoMapInstance | null>(null);
  const onChangeRef = useRef(onChange);
  const initialLocationRef = useRef({ latitude, longitude });
  const [error, setError] = useState<string>("");

  // Draws the pin and nothing else. Panning the map to the point that was just clicked
  // moves the rooftop out from under the cursor, so the next adjustment lands somewhere the
  // user did not aim at — which is the whole "ปักไม่ตรง" complaint. The viewport moves in
  // exactly two places: the initial centring below, and the ดูตำแหน่งหมุด button.
  const drawMarker = (location: { latitude: number; longitude: number }) => {
    const map = mapRef.current;
    if (!map || !window.longdo) return;

    map.Overlays.clear();
    map.Overlays.add(
      new window.longdo.Marker({ lat: location.latitude, lon: location.longitude }),
    );
  };

  const handleRecenterOnMarker = () => {
    if (latitude === null || longitude === null) return;
    mapRef.current?.location({ lat: latitude, lon: longitude }, true);
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_LONGDO_MAP_API_KEY;
    let mounted = true;

    if (!apiKey) {
      setError("ยังไม่ได้ตั้งค่า REACT_APP_LONGDO_MAP_API_KEY");
      return;
    }

    loadLongdoMap(apiKey)
      .then(() => {
        if (!mounted || !mapContainerRef.current || !window.longdo) return;

        const map = new window.longdo.Map({
          placeholder: mapContainerRef.current,
        });
        mapRef.current = map;
        const initialLocation = initialLocationRef.current;

        map.location(
          {
            lat: initialLocation.latitude ?? DEFAULT_CENTER.latitude,
            lon: initialLocation.longitude ?? DEFAULT_CENTER.longitude,
          },
          true,
        );

        if (
          initialLocation.latitude !== null &&
          initialLocation.longitude !== null
        ) {
          drawMarker({
            latitude: initialLocation.latitude,
            longitude: initialLocation.longitude,
          });
        }

        map.Event.bind("click", () => {
          const selected = map.location(window.longdo?.LocationMode.Pointer);
          if (!selected) return;
          onChangeRef.current({
            latitude: selected.lat,
            longitude: selected.lon,
          });
          drawMarker({ latitude: selected.lat, longitude: selected.lon });
        });
      })
      .catch(() => {
        if (mounted) setError("ไม่สามารถโหลด Longdo Map ได้");
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (latitude !== null && longitude !== null) {
      drawMarker({ latitude, longitude });
    }
  }, [latitude, longitude]);

  return (
    <Box>
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Box
        ref={mapContainerRef}
        sx={{
          height: 320,
          [compact]: { height: 220 },
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: (theme) => theme.palette.background.default,
        }}
      />
      <Box
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {latitude !== null && longitude !== null
            ? `Lat ${latitude.toFixed(6)}, Lng ${longitude.toFixed(6)}`
            : "คลิกบนแผนที่เพื่อปักหมุดตำแหน่งลูกค้า"}
        </Typography>
        {latitude !== null && longitude !== null ? (
          <Button size="small" onClick={handleRecenterOnMarker}>
            ดูตำแหน่งหมุด
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
